import {
  formatPercent,
  formatPrice,
  summarisePortfolio,
  type PositionView,
} from '@stockstop/shared';

/**
 * Das Portfolio in zwei Zeilen, unter der Überschrift.
 *
 * Oben, was heute drin steckt. Darunter, was übrig bliebe, wenn jeder Stop-Loss
 * auslöste — die Zahl, um derentwillen es dieses Werkzeug gibt. Sie beziffert,
 * worauf man sich durch das Setzen der Marken festgelegt hat.
 *
 * Beide Prozentwerte beziehen sich auf den Einstand, sind also nebeneinander
 * lesbar. Die Rechnung liegt in `shared` und ist dort geprüft; hier wird nur
 * dargestellt.
 */

const tone = (value: number | null): string =>
  value === null ? '' : value >= 0 ? 'tone-ok' : 'tone-danger';

export const PortfolioSummary = ({
  positions,
  currency,
}: {
  positions: PositionView[];
  currency: string | null;
}): React.JSX.Element | null => {
  const summe = summarisePortfolio(positions);

  /* Ohne einen einzigen Bestand gibt es nichts zusammenzufassen — eine Zeile aus
   * Gedankenstrichen wäre nur Platzverbrauch. */
  if (summe.marketValue === null) return null;

  return (
    <dl className="portfolio-summary">
      <div className="portfolio-summary__row">
        <dt>Wert</dt>
        <dd>
          <strong>{formatPrice(summe.marketValue, { currency, digits: 0 })}</strong>
          {summe.unrealisedPercent !== null ? (
            <span className={`portfolio-summary__percent ${tone(summe.unrealisedPercent)}`}>
              {formatPercent(summe.unrealisedPercent)}
            </span>
          ) : null}
        </dd>
      </div>

      <div
        className="portfolio-summary__row"
        title="Was das Portfolio noch wert wäre, wenn jeder Stop-Loss auslöste"
      >
        <dt>beim Stop</dt>
        <dd>
          {summe.stopLossValue === null ? (
            <span className="muted">kein Stop gesetzt</span>
          ) : (
            <>
              <strong>{formatPrice(summe.stopLossValue, { currency, digits: 0 })}</strong>
              {summe.stopLossPercent !== null ? (
                <span className={`portfolio-summary__percent ${tone(summe.stopLossPercent)}`}>
                  {formatPercent(summe.stopLossPercent)}
                </span>
              ) : null}
            </>
          )}
        </dd>
      </div>

      {/*
        Fehlt irgendwo ein Stop, ist die untere Summe unvollständig. Ein
        unvollständiger Betrag, der aussieht wie ein vollständiger, ist schlimmer
        als gar keiner — deshalb steht es dabei.
      */}
      {summe.ohneStop > 0 ? (
        <p className="portfolio-summary__hinweis">
          {summe.ohneStop === 1 ? 'Ein Wert hat' : `${summe.ohneStop} Werte haben`} noch keinen
          Stop-Loss und {summe.ohneStop === 1 ? 'ist' : 'sind'} in dieser Summe nicht enthalten.
        </p>
      ) : null}
    </dl>
  );
};
